/**
 * Dynamic Spinner Controller
 * Beautiful loading animations inspired by Claude Code and Codex
 */

import ora from 'ora';
import chalk from 'chalk';
import {
    muted,
    primary,
    warning,
    symbols,
    toolSymbol,
    mcpSymbol,
} from '../ui/theme/index.js';

/**
 * Gradient colors for breathing effect
 */
const GRADIENT_COLORS = [
    '#9D4EDD', // Purple
    '#7B2CBF', // Deep purple
    '#5A189A', // Violet
    '#3C096C', // Dark violet
    '#5A189A', // Back up
    '#7B2CBF',
    '#9D4EDD',
    '#C77DFF', // Light purple
    '#E0AAFF', // Lavender
    '#C77DFF',
];

/**
 * Custom spinner frames with wave effect
 */
const WAVE_FRAMES = [
    '⠋',
    '⠙',
    '⠹',
    '⠸',
    '⠼',
    '⠴',
    '⠦',
    '⠧',
    '⠇',
    '⠏',
];

const PULSE_FRAMES = [
    '●∙∙∙∙',
    '∙●∙∙∙',
    '∙∙●∙∙',
    '∙∙∙●∙',
    '∙∙∙∙●',
    '∙∙∙●∙',
    '∙∙●∙∙',
    '∙●∙∙∙',
];

const RIPPLE_FRAMES = [
    '◜ ',
    ' ◝',
    ' ◞',
    '◟ ',
];

/**
 * Spinner controller interface
 */
export interface SpinnerController {
    start: () => void;
    stop: () => void;
    clear: () => void;
    text: (message: string) => void;
    success: (message?: string) => void;
    fail: (message?: string) => void;
    isSpinning: () => boolean;
}

/**
 * Spinner type enum
 */
export type SpinnerType = 'thinking' | 'tool' | 'mcp' | 'loading';

const SPINNER_SAFETY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Create a thinking spinner with gradient animation
 */
export function createThinkingSpinner(): SpinnerController {
    let colorIdx = 0;

    const spinner = ora({
        text: chalk.dim('Thinking...'),
        spinner: {
            interval: 80,
            frames: WAVE_FRAMES,
        },
        color: 'magenta',
    });

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
        if (intervalId) { clearInterval(intervalId); intervalId = null; }
        if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    };

    return {
        start: () => {
            spinner.start();
            intervalId = setInterval(() => {
                colorIdx = (colorIdx + 1) % GRADIENT_COLORS.length;
                spinner.color = 'magenta';
                spinner.text = chalk.hex(GRADIENT_COLORS[colorIdx])('💭 Thinking...');
            }, 150);
            safetyTimer = setTimeout(() => { cleanup(); spinner.stop(); }, SPINNER_SAFETY_TIMEOUT_MS);
        },
        stop: () => { cleanup(); spinner.stop(); },
        clear: () => { cleanup(); spinner.stop(); process.stdout.write('\r\x1b[K'); },
        text: (message: string) => {
            spinner.text = chalk.hex(GRADIENT_COLORS[colorIdx])(message);
        },
        success: (message?: string) => { cleanup(); spinner.succeed(message); },
        fail: (message?: string) => { cleanup(); spinner.fail(message); },
        isSpinning: () => spinner.isSpinning,
    };
}

/**
 * Create a tool call spinner with pulse animation
 */
export function createToolCallSpinner(toolName: string): SpinnerController {
    const spinner = ora({
        text: muted(`${toolSymbol()} ${primary(toolName)}...`),
        spinner: {
            interval: 100,
            frames: PULSE_FRAMES,
        },
        color: 'cyan',
    });

    return {
        start: () => spinner.start(),
        stop: () => spinner.stop(),
        clear: () => {
            spinner.stop();
            process.stdout.write('\r\x1b[K');
        },
        text: (message: string) => {
            spinner.text = muted(`${toolSymbol()} ${primary(message)}...`);
        },
        success: (_message?: string) => {
            spinner.succeed(muted(`${symbols.success} Tool ${primary(toolName)} completed`));
        },
        fail: (message?: string) => {
            spinner.fail(muted(`${symbols.error} Tool ${primary(toolName)} failed${message ? `: ${message}` : ''}`));
        },
        isSpinning: () => spinner.isSpinning,
    };
}

/**
 * Create an MCP tool call spinner with ripple animation
 */
export function createMcpSpinner(serverName: string, toolName: string): SpinnerController {
    const spinner = ora({
        text: muted(`${mcpSymbol()} [${warning(serverName)}] ${primary(toolName)}...`),
        spinner: {
            interval: 120,
            frames: RIPPLE_FRAMES,
        },
        color: 'yellow',
    });

    return {
        start: () => spinner.start(),
        stop: () => spinner.stop(),
        clear: () => {
            spinner.stop();
            process.stdout.write('\r\x1b[K');
        },
        text: (message: string) => {
            spinner.text = message;
        },
        success: (_message?: string) => {
            spinner.succeed(
                muted(`${symbols.success} [${warning(serverName)}] ${primary(toolName)} completed`)
            );
        },
        fail: (message?: string) => {
            spinner.fail(
                muted(`${symbols.error} [${warning(serverName)}] ${primary(toolName)} failed${message ? `: ${message}` : ''}`)
            );
        },
        isSpinning: () => spinner.isSpinning,
    };
}

/**
 * Create a generic loading spinner
 */
export function createLoadingSpinner(message: string = 'Loading...'): SpinnerController {
    const spinner = ora({
        text: chalk.dim(message),
        spinner: 'dots',
        color: 'blue',
    });

    return {
        start: () => spinner.start(),
        stop: () => spinner.stop(),
        clear: () => {
            spinner.stop();
            process.stdout.write('\r\x1b[K');
        },
        text: (msg: string) => {
            spinner.text = chalk.dim(msg);
        },
        success: (msg?: string) => spinner.succeed(msg),
        fail: (msg?: string) => spinner.fail(msg),
        isSpinning: () => spinner.isSpinning,
    };
}

/**
 * Agent analyzing spinner with rotating messages and ∩ brand animation
 * Uses rotating messages and gradient colors for enhanced waiting experience
 */
export function createAgentAnalyzingSpinner(): SpinnerController {
    // Rotating messages for agent analysis
    const MESSAGES = [
        '∩ Analyzing your prompt...',
        '∩ Understanding context...',
        '∩ Planning approach...',
        '∩ Preparing response...',
        '◉ Deep thinking...',
        '∩ Almost there...',
    ];

    // Arch animation frames
    const ARCH_FRAMES = ['∩', '⌒', '︵', '⌓', '∩'];

    // Gradient colors for breathing effect
    const COLORS = [
        '#0077FF', // Paean Blue
        '#00F0FF', // Highlight cyan
        '#8b5cf6', // Violet
        '#a78bfa', // Light violet
        '#0077FF', // Back to blue
    ];

    let messageIdx = 0;
    let colorIdx = 0;
    let frameIdx = 0;

    const spinner = ora({
        text: chalk.hex(COLORS[0])(MESSAGES[0]),
        spinner: {
            interval: 120,
            frames: WAVE_FRAMES,
        },
        color: 'cyan',
    });

    let messageInterval: ReturnType<typeof setInterval> | null = null;
    let colorInterval: ReturnType<typeof setInterval> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;

    const updateDisplay = () => {
        const archSymbol = ARCH_FRAMES[frameIdx];
        const message = MESSAGES[messageIdx].replace(/^[∩◉]/, archSymbol);
        spinner.text = chalk.hex(COLORS[colorIdx])(message);
    };

    const cleanup = () => {
        if (messageInterval) { clearInterval(messageInterval); messageInterval = null; }
        if (colorInterval) { clearInterval(colorInterval); colorInterval = null; }
        if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    };

    return {
        start: () => {
            spinner.start();
            messageInterval = setInterval(() => {
                messageIdx = (messageIdx + 1) % MESSAGES.length;
                updateDisplay();
            }, 2500);
            colorInterval = setInterval(() => {
                colorIdx = (colorIdx + 1) % COLORS.length;
                frameIdx = (frameIdx + 1) % ARCH_FRAMES.length;
                updateDisplay();
            }, 200);
            safetyTimer = setTimeout(() => { cleanup(); spinner.stop(); }, SPINNER_SAFETY_TIMEOUT_MS);
        },
        stop: () => { cleanup(); spinner.stop(); },
        clear: () => { cleanup(); spinner.stop(); process.stdout.write('\r\x1b[K'); },
        text: (message: string) => {
            spinner.text = chalk.hex(COLORS[colorIdx])(message);
        },
        success: (message?: string) => { cleanup(); spinner.succeed(message); },
        fail: (message?: string) => { cleanup(); spinner.fail(message); },
        isSpinning: () => spinner.isSpinning,
    };
}

/**
 * Thinking indicator with scrolling highlight bar animation
 * Displays a horizontal line above the thinking text with a glowing segment that moves
 */
export interface ThinkingAnimationController {
    start: () => void;
    stop: () => void;
    clear: () => void;
    updateText: (text: string) => void;
    isRunning: () => boolean;
}

export function createThinkingAnimation(): ThinkingAnimationController {
    const PAEAN_BLUE = '#0077FF';
    const PAEAN_LIGHT = '#00F0FF';
    const MUTED = '#71717a';

    // Use full terminal width for the bar
    const getBarWidth = () => Math.min(process.stdout.columns || 80, 60);
    const HIGHLIGHT_WIDTH = 8;

    let position = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;
    let isActive = false;
    let currentText = '💭 Thinking...';

    const buildBar = (pos: number, width: number): string => {
        let bar = '';
        for (let i = 0; i < width; i++) {
            const distFromHighlight = Math.abs(i - pos);
            if (distFromHighlight === 0) {
                bar += chalk.hex(PAEAN_LIGHT)('━');
            } else if (distFromHighlight <= 2) {
                bar += chalk.hex(PAEAN_BLUE)('━');
            } else if (distFromHighlight <= HIGHLIGHT_WIDTH / 2) {
                bar += chalk.hex(MUTED)('─');
            } else {
                bar += chalk.hex(MUTED)('─');
            }
        }
        return bar;
    };

    const render = () => {
        const barWidth = getBarWidth();
        const bar = buildBar(position % barWidth, barWidth);
        const text = `${chalk.hex(PAEAN_BLUE)('❯')} ${chalk.hex(MUTED)(currentText)}`;
        process.stdout.write(`\r\x1b[K${bar}\n\r\x1b[K${text}\x1b[A\r`);
    };

    const cleanup = () => {
        if (intervalId) { clearInterval(intervalId); intervalId = null; }
        if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    };

    return {
        start: () => {
            if (isActive) return;
            isActive = true;
            position = 0;

            console.log('');
            render();

            intervalId = setInterval(() => {
                position = (position + 1) % getBarWidth();
                render();
            }, 40);
            safetyTimer = setTimeout(() => { cleanup(); isActive = false; }, SPINNER_SAFETY_TIMEOUT_MS);
        },
        stop: () => {
            cleanup();
            isActive = false;
        },
        clear: () => {
            cleanup();
            isActive = false;
            process.stdout.write('\r\x1b[K\x1b[B\r\x1b[K\x1b[A');
        },
        updateText: (text: string) => {
            currentText = text;
            if (isActive) {
                render();
            }
        },
        isRunning: () => isActive,
    };
}
